import { useState, useEffect, useMemo } from 'react'
import { campaignsAPI, cohortsAPI } from '../../services/api'
import { getTodayChile } from '../../utils/dateBusinessDays'
import CohortPreviewSummary, { HIGH_VOLUME_THRESHOLD } from './CohortPreviewSummary'

/**
 * Modal de 3 pasos para agregar prospectos seleccionados a una campaña en una cohorte:
 * 1. Elegir campaña madre (active)
 * 2. Elegir fecha de inicio (calendar mensual con heatmap)
 * 3. Preview granular + confirmar
 *
 * Props:
 *   contactIds: number[] (ids de prospectos seleccionados)
 *   onClose: () => void
 *   onSuccess: (result) => void
 *   preselectedCampaignId?: string (si viene de Vista Campañas, salta Step 1)
 */
export default function BulkAddToCampaignPicker({ contactIds, onClose, onSuccess, preselectedCampaignId = null }) {
  const [step, setStep] = useState(preselectedCampaignId ? 2 : 1)
  const [campaigns, setCampaigns] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCampaign, setSelectedCampaign] = useState(null)
  const [selectedDate, setSelectedDate] = useState(null)
  const [calendarMonth, setCalendarMonth] = useState(() => { const d = new Date(); d.setDate(1); return d })
  const [calendarData, setCalendarData] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Stats explícitos del preview-bulk (Task 9): se cargan al entrar a Step 3.
  const [previewStats, setPreviewStats] = useState(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState(null)

  useEffect(() => {
    if (step === 1) {
      campaignsAPI.getCampaigns().then(d => {
        setCampaigns((d.campaigns || []).filter(c => c.status === 'active'))
      }).catch(e => setError(e.message))
    }
  }, [step])

  useEffect(() => {
    if (preselectedCampaignId && !selectedCampaign) {
      campaignsAPI.getCampaign(preselectedCampaignId).then(d => {
        const camp = d.campaign
        setSelectedCampaign(camp)
        // Instant: el backend ignora la fecha (siempre cohorte nueva running/NOW).
        // Saltamos el calendario y fijamos hoy-Chile para el preview.
        if (camp?.instant_mode === true) {
          setSelectedDate(formatDate(getTodayChile()))
          setStep(3)
        }
      })
    }
  }, [preselectedCampaignId, selectedCampaign])

  useEffect(() => {
    if (step === 2) {
      const from = formatDate(calendarMonth)
      const to = formatDate(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 0))
      cohortsAPI.getCalendar({ from, to, onlyMine: false }).then(d => setCalendarData(d.cohorts || []))
    }
  }, [step, calendarMonth])

  // Fetch stats explícitos del preview-bulk al llegar a Step 3 con todos los inputs listos.
  useEffect(() => {
    if (step !== 3) return
    if (!selectedCampaign?.id) return
    if (!selectedDate) return
    if (!contactIds || contactIds.length === 0) return

    let cancelled = false
    setPreviewLoading(true)
    setPreviewError(null)
    setPreviewStats(null)

    cohortsAPI.previewBulk({
      campaignId: selectedCampaign.id,
      startDate: selectedDate,
      contactIds
    })
      .then(stats => {
        if (cancelled) return
        setPreviewStats(stats)
      })
      .catch(e => {
        if (cancelled) return
        setPreviewError(e?.response?.data?.error || e.message)
      })
      .finally(() => {
        if (cancelled) return
        setPreviewLoading(false)
      })

    return () => { cancelled = true }
  }, [step, selectedCampaign?.id, selectedDate, contactIds])

  const goToPreview = () => {
    setError(null)
    setStep(3)
  }

  const confirm = async () => {
    setLoading(true)
    try {
      // Modo instantáneo: NUNCA reusar una cohorte collecting programada — siempre
      // crear vía cohortsAPI.create, que en el backend toma el path instant
      // (start_at=NOW, running). Si no, los contactos quedarían agendados a la fecha
      // de la cohorte vieja y la nota "se envía de inmediato" sería falsa.
      const existingCohort = selectedCampaign.instant_mode === true
        ? null
        : calendarData.find(co => co.campaign_id === selectedCampaign.id && (co.start_date || '').substring(0, 10) === selectedDate && co.status === 'collecting')
      let cohortId
      if (existingCohort) {
        cohortId = existingCohort.id
      } else {
        const created = await cohortsAPI.create({ campaignId: selectedCampaign.id, startDate: selectedDate })
        cohortId = created.cohort.id
      }
      const result = await cohortsAPI.addEnrollments(cohortId, contactIds)
      onSuccess(result)
      onClose()
    } catch (e) { setError(e.response?.data?.error || e.message) }
    finally { setLoading(false) }
  }

  // Instant: undefined ≠ true (campaña sin el flag = programada).
  const isInstant = selectedCampaign?.instant_mode === true
  const existingCohort = isInstant
    ? null
    : calendarData.find(co => co.campaign_id === selectedCampaign?.id && (co.start_date || '').substring(0, 10) === selectedDate && co.status === 'collecting')
  const isHighVolume = previewStats && previewStats.total >= HIGH_VOLUME_THRESHOLD
  const confirmDisabled = loading || !previewStats || previewStats.ready === 0

  // En instant el calendario se salta: el flujo real es 2 pasos (campaña → confirmación).
  const totalSteps = isInstant ? 2 : 3
  const displayStep = isInstant && step === 3 ? 2 : step

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-surface-dark rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden flex flex-col h-[600px] max-h-[85vh]">
        <div className="bg-gradient-to-r from-purple-600 via-fuchsia-600 to-pink-600 px-6 py-4 flex-shrink-0">
          <h3 className="text-white font-semibold text-lg">📢 Agregar a campaña — Paso {displayStep} de {totalSteps}</h3>
          <p className="text-purple-100 text-sm">{contactIds.length} prospecto(s) seleccionado(s)</p>
        </div>

        <div className="p-6 flex-1 min-h-0 overflow-y-auto">
          {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-xl text-sm">{error}</div>}

          {step === 1 && (
            <Step1 campaigns={campaigns} searchTerm={searchTerm} onSearch={setSearchTerm} onPick={async (c) => {
              // Fetch detalle (incluye steps) para que Step2 pueda usar send_hour/send_minute reales del paso 1
              let campaign = c
              try {
                const detail = await campaignsAPI.getCampaign(c.id)
                campaign = detail.campaign || c
              } catch {
                campaign = c
              }
              setSelectedCampaign(campaign)
              if (campaign?.instant_mode === true) {
                // Instant: el backend ignora la fecha; saltamos el calendario y
                // fijamos hoy-Chile para que el preview use una fecha veraz.
                setSelectedDate(formatDate(getTodayChile()))
                setStep(3)
              } else {
                // Programada: limpiar la fecha para no arrastrar un hoy-auto de una
                // campaña instant elegida antes (el calendario debe partir sin selección).
                setSelectedDate(null)
                setStep(2)
              }
            }} />
          )}

          {step === 2 && selectedCampaign && (
            <>
              {selectedCampaign.instant_mode === true && (
                <div className="mb-3 px-3 py-2 rounded-xl bg-amber-50 border border-amber-300 text-amber-800 text-sm dark:bg-amber-900/20 dark:border-amber-700 dark:text-amber-300">
                  ⚡ <strong>Modo instantáneo:</strong> los pasos se enviarán de inmediato al inscribir (encadenados). La fecha elegida se ignora.
                </div>
              )}
              <Step2 campaign={selectedCampaign} month={calendarMonth} onMonthChange={setCalendarMonth} calendarData={calendarData} onPickDate={(d) => setSelectedDate(d)} selectedDate={selectedDate} />
            </>
          )}

          {step === 3 && (
            <div>
              <h4 className="font-medium mb-2">Confirmación</h4>
              {isInstant ? (
                <p className="text-sm text-gray-700 dark:text-gray-200 mb-2">
                  Vas a inscribir prospectos a <strong>{selectedCampaign?.name}</strong>.
                </p>
              ) : (
                <p className="text-sm text-gray-700 dark:text-gray-200 mb-2">
                  Vas a inscribir prospectos a <strong>{selectedCampaign?.name}</strong> para el día <strong>{selectedDate}</strong>.
                </p>
              )}
              {isInstant ? (
                <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-xl text-sm text-amber-800 dark:text-amber-300">
                  ⚡ <strong>Envío inmediato:</strong> los pasos parten al confirmar (encadenados, ~40s entre cada uno).
                </div>
              ) : existingCohort ? (
                <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-xl text-sm text-blue-800 dark:text-blue-200">
                  🔗 Se sumarán a la cohorte ya existente de ese día (<strong>{existingCohort.enrollment_count}</strong> inscrito(s) previo(s)).
                </div>
              ) : (
                <div className="mb-4 p-3 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-700 rounded-xl text-sm text-emerald-800 dark:text-emerald-200">
                  ✨ Se creará una nueva cohorte para esa fecha.
                </div>
              )}

              {previewLoading && (
                <div className="text-gray-500 dark:text-gray-400 text-sm">Calculando preview...</div>
              )}
              {previewError && (
                <div className="text-red-600 dark:text-red-400 text-sm">Error: {previewError}</div>
              )}
              {previewStats && (
                <CohortPreviewSummary stats={previewStats} />
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center bg-gray-50 dark:bg-surface-dark-elev flex-shrink-0">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-dark-hover rounded-xl">Cancelar</button>
          <div className="flex gap-2">
            {step > 1 && <button onClick={() => setStep(isInstant ? 1 : step - 1)} className="px-4 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-surface-dark-hover rounded-xl">Volver</button>}
            {step === 2 && <button disabled={!selectedDate || loading} onClick={goToPreview} className="px-5 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-medium disabled:opacity-50">Continuar</button>}
            {step === 3 && (
              <button
                type="button"
                onClick={confirm}
                disabled={confirmDisabled}
                className={`px-5 py-2 rounded-xl font-medium text-white transition ${
                  confirmDisabled
                    ? 'bg-gray-400 cursor-not-allowed'
                    : (isHighVolume
                        ? 'bg-amber-600 hover:bg-amber-700'
                        : 'bg-purple-600 hover:bg-purple-700')
                }`}
                title={
                  isHighVolume
                    ? `Estás por inscribir más de ${HIGH_VOLUME_THRESHOLD} contactos.`
                    : ''
                }
              >
                {loading
                  ? 'Inscribiendo…'
                  : (
                    <>
                      {isHighVolume && '⚠️ '}
                      Confirmar {previewStats?.ready ?? 0} inscripciones
                    </>
                  )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function Step1({ campaigns, searchTerm, onSearch, onPick }) {
  const filtered = campaigns.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()))
  return (
    <div>
      <input type="text" placeholder="Buscar campaña…" value={searchTerm} onChange={e => onSearch(e.target.value)}
        className="w-full px-4 py-2 border border-gray-300 dark:border-surface-dark-border dark:bg-surface-dark dark:text-gray-100 dark:placeholder-gray-500 rounded-xl mb-4" autoFocus />
      <div className="space-y-2">
        {filtered.map(c => (
          <button key={c.id} onClick={() => onPick(c)} className="w-full text-left p-4 border border-gray-200 dark:border-surface-dark-border rounded-xl text-gray-900 dark:text-gray-100 hover:border-purple-400 dark:hover:border-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/30 transition">
            <div className="font-medium">{c.name}</div>
            {c.description && <div className="text-sm text-gray-500 dark:text-gray-400 truncate">{c.description}</div>}
          </button>
        ))}
        {filtered.length === 0 && <div className="text-center text-gray-400 dark:text-gray-500 py-8">Sin resultados</div>}
      </div>
    </div>
  )
}

function Step2({ campaign, month, onMonthChange, calendarData, onPickDate, selectedDate }) {
  const days = useMemo(() => {
    const first = new Date(month.getFullYear(), month.getMonth(), 1)
    const last = new Date(month.getFullYear(), month.getMonth() + 1, 0)
    const startWeekDay = (first.getDay() + 6) % 7
    const arr = []
    for (let i = 0; i < startWeekDay; i++) arr.push(null)
    for (let d = 1; d <= last.getDate(); d++) arr.push(new Date(month.getFullYear(), month.getMonth(), d))
    return arr
  }, [month])

  const cohortsByDate = useMemo(() => {
    const map = {}
    // start_date viene del backend como timestamp con sufijo Z (ej "2026-05-30T00:00:00.000Z");
    // normalizar a 'YYYY-MM-DD' para que matchee con formatDate(d) y se aplique el heatmap.
    for (const co of calendarData) {
      const key = (co.start_date || '').substring(0, 10)
      if (!key) continue
      ;(map[key] = map[key] || []).push(co)
    }
    return map
  }, [calendarData])

  const heatmapColor = (cohorts) => {
    if (!cohorts || cohorts.length === 0) return ''
    const total = cohorts.reduce((s, c) => s + (c.enrollment_count || 0), 0)
    if (total <= 50) return 'bg-green-100 border-green-300 dark:bg-green-900/30 dark:border-green-700'
    if (total <= 200) return 'bg-amber-100 border-amber-300 dark:bg-amber-900/30 dark:border-amber-700'
    return 'bg-red-100 border-red-300 dark:bg-red-900/30 dark:border-red-700'
  }

  // Lee send_hour/send_minute del paso 1 activo de la campaña; fallback 10:00.
  // Usado para el lock check del calendar (mismo cálculo que el backend hace
  // en CohortService._calculateStartAt + start_at >= NOW + 1h).
  const firstStep = (campaign.steps || []).find(s => s.step_number === 1 && s.is_active !== false) || (campaign.steps || [])[0]
  const startHour = firstStep?.send_hour ?? 10
  const startMinute = firstStep?.send_minute ?? 0

  const isLocked = (date) => {
    const start = new Date(date.getFullYear(), date.getMonth(), date.getDate(), startHour, startMinute)
    return start.getTime() < Date.now() + 30 * 60 * 1000 // 30 min lock window
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <button onClick={() => onMonthChange(new Date(month.getFullYear(), month.getMonth() - 1, 1))} className="px-3 py-1 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-dark-hover rounded-lg">◀</button>
        <h4 className="font-medium text-gray-900 dark:text-gray-100">{month.toLocaleString('es-CL', { month: 'long', year: 'numeric' })}</h4>
        <button onClick={() => onMonthChange(new Date(month.getFullYear(), month.getMonth() + 1, 1))} className="px-3 py-1 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-dark-hover rounded-lg">▶</button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-xs text-gray-500 dark:text-gray-400 mb-2">
        {['Lu','Ma','Mi','Ju','Vi','Sa','Do'].map(d => <div key={d} className="text-center py-1 font-medium">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((d, idx) => {
          if (!d) return <div key={idx} />
          const dStr = formatDate(d)
          const cohorts = cohortsByDate[dStr] || []
          const sameCampaign = cohorts.find(co => co.campaign_id === campaign.id && co.status === 'collecting')
          const sameCampaignLocked = cohorts.find(co => co.campaign_id === campaign.id && co.status !== 'collecting')
          const locked = isLocked(d) || sameCampaignLocked
          const isSelected = selectedDate === dStr
          return (
            <button key={idx} disabled={locked} onClick={() => onPickDate(dStr)}
              className={`relative aspect-square flex flex-col items-center justify-center text-sm border rounded-lg text-gray-800 dark:text-gray-100
                ${locked ? 'bg-gray-100 dark:bg-surface-dark-hover text-gray-400 dark:text-gray-600 cursor-not-allowed' : `${heatmapColor(cohorts)} hover:border-purple-400`}
                ${isSelected ? 'ring-2 ring-purple-500' : ''}`}
              title={locked ? 'Bloqueado: requiere ≥30 min de antelación o ya hay cohorte cerrada/en curso' : (sameCampaign ? `Cohorte existente: ${sameCampaign.enrollment_count} inscrito(s)` : '')}>
              <span>{d.getDate()}</span>
              {sameCampaign && <span className="text-[10px] text-blue-700 dark:text-blue-300">+{sameCampaign.enrollment_count}</span>}
              {!sameCampaign && cohorts.length > 0 && <span className="w-1 h-1 rounded-full bg-purple-500 mt-0.5" />}
            </button>
          )
        })}
      </div>

      {/* Leyenda heatmap */}
      <div className="mt-4 flex flex-wrap items-center gap-3 text-[11px] text-gray-600 dark:text-gray-300">
        <span className="font-medium text-gray-500 dark:text-gray-400">Volumen del día:</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-green-400 border border-green-500" /> Bajo (≤50)</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-amber-400 border border-amber-500" /> Medio (51-200)</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-red-400 border border-red-500" /> Alto (200+)</span>
        <span className="inline-flex items-center gap-1.5 ml-2"><span className="w-3 h-3 rounded bg-gray-400 dark:bg-slate-500 border border-gray-500 dark:border-slate-400" /> Bloqueado</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-purple-500" /> Otra cohorte ese día</span>
      </div>
    </div>
  )
}

function formatDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
}
